import {
    ContainerRef,
    DecisionTreeContent,
    DecisionTreeNode,
    NodeAnswerDisplayType,
    RichTextContent,
    IUserActivity,
} from "@amzn/ask-legal-domain";
import * as React from "react";
import {
    Box,
    Button,
    ColumnLayout,
    SpaceBetween,
    Spinner
} from "@amzn/awsui-components-react";
import { RichTextContentView } from "./RichTextContentView";
import { ButtonAnswer } from "../../decision-tree-container/view-components/ButtonAnswer";
import { DropdownAnswers } from "../../decision-tree-container/view-components/DropdownAnswers";
import { PathSummary, PathStep } from "../../decision-tree-container/view-components/PathSummary";
import { AppContext } from "../../../setup/context";
import { useAPI2 } from "../../../hooks/api-hook";
import { ErrorFlashbar } from "../../common/ErrorFlashbar";
import { useCookies } from "react-cookie";
import { updateSession } from "../../../utils/session-management-utils";

export const DecisionTreeContentView = (props: {
    content: DecisionTreeContent;
    containerRef: ContainerRef;
}) => {
    const context = React.useContext(AppContext);
    const loadNodeApiRunner = useAPI2(
        context.getDecisionTreeNodeAPI().get
    );
    const recordUserActivityRunner = useAPI2(
        context.getUserActivityAPI().record
    );

    const [viewContent, setViewContent] = React.useState<React.ReactNode>(
        <RichTextContentView
            content={
                props.content.welcomeText
                    ? props.content.welcomeText
                    : RichTextContent.create("Click on start to begin")
            }
        />
    );

    const [actionButtons, setActionButtons] = React.useState<{answer: string; nodeId: string}[]>([{
        answer: "Start",
        nodeId: props.content.rootNodeId
    }]);
    const [parentNodeId, setParentNodeId] = React.useState<string>(null);
    const [currentNodeId, setCurrentNodeId] = React.useState<string>(null);
    const [answerDisplayStyle, setAnswerDisplayStyle] = React.useState<NodeAnswerDisplayType>(
        NodeAnswerDisplayType.Buttons
    );
    const [pathSteps, setPathSteps] = React.useState<PathStep[]>([]);
    const [cookies, setCookies] = useCookies(["sid", "sts"]);

    const recordDecisionTreeNodeView = (nodeId?: string) => {
        const sessionId = updateSession(cookies, { setCookies });
        const currentNode = nodeId ? props.content.allNodes.find(node => node.id === nodeId) : null;
        if (currentNode) {
            recordUserActivityRunner.invoke(
                IUserActivity.RecordInput.createDTViewRecordInput({
                    sessionId: sessionId,
                    url: window.location.href,
                    containerRef: props.containerRef,
                    decisionTreeNode: currentNode,
                    isLeafNode: DecisionTreeNode.isLeafNode(currentNode)
                })
            );
        }
    };

    // Ref to track the answer text the user selected (set before API call)
    const selectedAnswerTextRef = React.useRef<string | undefined>(undefined);
    // Ref to track navigation direction
    const navigationTypeRef = React.useRef<"start" | "forward" | "back">("start");

    const restartDecisionTree = () => {
        setViewContent(
            <RichTextContentView
                content={
                    props.content.welcomeText
                        ? props.content.welcomeText
                        : RichTextContent.create("Click on start to begin")
                }
            />
        );
        setActionButtons([{
            answer: "Start",
            nodeId: props.content.rootNodeId
        }]);
        setCurrentNodeId(null);
        setParentNodeId(null);
        setAnswerDisplayStyle(NodeAnswerDisplayType.Buttons);
        setPathSteps([]);
    };

    const getNode = (nodeId: string) => {
        // Determine navigation type and capture answer text
        if (!currentNodeId) {
            // First click (Start button)
            navigationTypeRef.current = "start";
            selectedAnswerTextRef.current = undefined;
        } else if (nodeId === parentNodeId) {
            // Going back
            navigationTypeRef.current = "back";
            selectedAnswerTextRef.current = undefined;
        } else {
            // Going forward - find the answer text from current action buttons
            navigationTypeRef.current = "forward";
            const selectedButton = actionButtons.find(btn => btn.nodeId === nodeId);
            // Only update the ref when a matching button is found, so the previously
            // captured value is preserved across retries (error handler clears actionButtons)
            if (selectedButton) {
                selectedAnswerTextRef.current = selectedButton.answer;
            }
        }

        loadNodeApiRunner.invoke({
            containerRef: props.containerRef,
            nodeId: nodeId
        });
    };

    const getAnswerLayout = () => {
        if (answerDisplayStyle === NodeAnswerDisplayType.Dropdown) {
            return <div className="center-div">
                <DropdownAnswers
                    answers={actionButtons}
                    onAnswerSelected={getNode}
                    disabled={loadNodeApiRunner.status !== "Succeeded"}
                />
            </div>;
        }
        if (answerDisplayStyle === NodeAnswerDisplayType.Buttons) {
            const answerButtons = actionButtons.map(x => <ButtonAnswer
                nodeId={x.nodeId}
                answer={x.answer}
                onClickHandler={getNode}
                loading={loadNodeApiRunner.status === "Running"}
                disabled={loadNodeApiRunner.status === "Error"}
            />);
            if (actionButtons.length === 1) {
                return <div className="vertical-center">{answerButtons}</div>;
            } else if (actionButtons.length === 2) {
                return <ColumnLayout columns={2}>
                    {answerButtons.map(val => <div className="vertical-center">{val}</div>)}
                </ColumnLayout>;
            } else {
                return <ColumnLayout columns={3} borders="vertical">
                    {answerButtons}
                </ColumnLayout>;
            }
        }
    };

    React.useEffect(() => {
        if (loadNodeApiRunner.status === "Succeeded") {
            setViewContent(<RichTextContentView content={loadNodeApiRunner.output.content} />);
            const currentNode = props.content.allNodes.find(node => node.id === loadNodeApiRunner.input.nodeId);
            setActionButtons(currentNode.children);
            setParentNodeId(currentNode.parentNodeId);
            setCurrentNodeId(loadNodeApiRunner.input.nodeId);
            setAnswerDisplayStyle(currentNode.answerDisplayType || NodeAnswerDisplayType.Buttons);
            recordDecisionTreeNodeView(currentNode.id);

            // Track path for summary
            const navType = navigationTypeRef.current;
            if (navType === "start") {
                // Starting the tree - first node (root)
                setPathSteps([{
                    questionText: currentNode.overviewText,
                    nodeId: currentNode.id,
                }]);
            } else if (navType === "forward") {
                // Moving forward - update previous step with answer, add new step
                setPathSteps(prev => {
                    const updated = [...prev];
                    // Set the answer on the last step (the question we just answered)
                    if (updated.length > 0) {
                        updated[updated.length - 1] = {
                            ...updated[updated.length - 1],
                            selectedAnswer: selectedAnswerTextRef.current,
                        };
                    }
                    // Add the new node as a new step
                    updated.push({
                        questionText: currentNode.overviewText,
                        nodeId: currentNode.id,
                    });
                    return updated;
                });
            } else if (navType === "back") {
                // Going back - remove the last step and clear the answer on the new last step
                setPathSteps(prev => {
                    if (prev.length <= 1) return [];
                    const updated = prev.slice(0, -1);
                    // Clear the answer on the step we're going back to
                    updated[updated.length - 1] = {
                        ...updated[updated.length - 1],
                        selectedAnswer: undefined,
                    };
                    return updated;
                });
            }
        } else if (loadNodeApiRunner.status === "Running") {
            setViewContent(<Box textAlign="center" variant="awsui-key-label">
                <Spinner /> <em>Fetching Content</em>
            </Box>);
        } else if (loadNodeApiRunner.status === "Error") {
            setViewContent(
                <Box variant="div" textAlign="center" margin="xxxl" padding="xxxl">
                    <ErrorFlashbar error={loadNodeApiRunner.err} action={
                        <Button onClick={() => getNode(loadNodeApiRunner.input.nodeId)}>Retry</Button>
                    }/>
                </Box>
            );
            setActionButtons([]);
            setParentNodeId(currentNodeId);
            setCurrentNodeId(loadNodeApiRunner.input.nodeId);
            setAnswerDisplayStyle(NodeAnswerDisplayType.Buttons);
        }
    }, [loadNodeApiRunner.status]);

    // Determine if the user has reached a leaf node (end of tree)
    const isAtLeafNode = currentNodeId && actionButtons.length === 0 && loadNodeApiRunner.status === "Succeeded";

    return <>
        <SpaceBetween size="m" direction="vertical">
            <Box textAlign="right">
                {currentNodeId && <Button
                    iconName="refresh"
                    onClick={restartDecisionTree}
                    loading={loadNodeApiRunner.status === "Running"}
                    disabled={loadNodeApiRunner.status !== "Succeeded"}
                >Restart</Button>}
                {currentNodeId && parentNodeId && <ButtonAnswer
                    nodeId={parentNodeId}
                    answer="Go Back"
                    onClickHandler={getNode}
                    loading={loadNodeApiRunner.status === "Running"}
                    disabled={loadNodeApiRunner.status === "Error"}
                    actionType="previous"
                />}
            </Box>
            <div style={{minHeight: "200px"}}>
                {viewContent}
            </div>
            {actionButtons.length > 0 && <hr />}
            <Box textAlign="center">
                {getAnswerLayout()}
            </Box>
            {isAtLeafNode && pathSteps.length > 0 && (
                <PathSummary pathSteps={pathSteps} />
            )}
        </SpaceBetween>
    </>;
};
